import { useMemo } from 'react';
import { useCustomFieldsData, UseCustomFieldsDataReturn } from './useCustomFieldsData';
import { useCustomFieldsForm, UseCustomFieldsFormReturn } from './useCustomFieldsForm';
import { useCustomFieldsActions, UseCustomFieldsActionsReturn } from './useCustomFieldsActions';

export interface UseCustomFieldsReturn extends UseCustomFieldsDataReturn, UseCustomFieldsFormReturn, UseCustomFieldsActionsReturn {
    // Combined state
    isReady: boolean;
    hasData: boolean;
    isEmpty: boolean;

    // Utility methods
    refresh: () => void;
    reset: () => void;
}

export interface UseCustomFieldsOptions {
    enabled?: boolean;
    onSuccess?: () => void;
}

/**
 * Master hook that combines all custom fields functionality
 * Provides complete interface for managing custom fields in components
 */
export const useCustomFields = (userId: number, options: UseCustomFieldsOptions = {}): UseCustomFieldsReturn => {
    const { enabled = true, onSuccess } = options;

    // Data fetching
    const dataHook = useCustomFieldsData(userId, { enabled });
    const { definitions, userFields, isLoading, isError } = dataHook;

    // Form management
    const formHook = useCustomFieldsForm(definitions, userFields);

    // API actions
    const actionsHook = useCustomFieldsActions(userId, definitions, onSuccess);

    // Combined computed state
    const isReady = useMemo(() => !isLoading && !isError && definitions.length > 0, [isLoading, isError, definitions]);
    const hasData = useMemo(() => userFields.length > 0, [userFields]);
    const isEmpty = useMemo(() => isReady && !hasData, [isReady, hasData]);

    // Combined utility methods
    const refresh = () => {
        dataHook.refetch();
    };

    const reset = () => {
        formHook.resetFieldSelection();
        actionsHook.resetError();
    };

    return {
        // Data
        ...dataHook,

        // Form
        ...formHook,

        // Actions
        ...actionsHook,

        // Combined state
        isReady,
        hasData,
        isEmpty,

        // Utility
        refresh,
        reset,
    };
};

export default useCustomFields;
